import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import PoolAdminRoute from '../components/PoolAdminRoute'
import LoadingSpinner from '../components/LoadingSpinner'
import { 
  ClockIcon,
  TrophyIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
  EyeIcon,
  Cog6ToothIcon,
  TrashIcon,
  ChevronRightIcon,
  HomeIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import PoolsApi from '../services/poolsApi'

const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'https://api.midwestrailbirds.com/v1'

interface Horse {
  horse_id: string
  post_position: string
  post_position_color?: {
    background_color: string
    text_color: string
    name: string
  }
  horse_name: string
  jockey: string
  trainer: string
  weight: string
  medication?: string
  age_sex: string
}

interface Race {
  raceNumber: number
  postTime: string
  track: string
  raceType: string
  conditions: string
  purse: string
  distance?: string
  status: 'scheduled' | 'running' | 'final'
  entries: Horse[]
  scratches: string[]
  winner?: string
  placeFinisher?: string
  showFinisher?: string
  entryCount: number
}

interface Participant {
  userId: string
  name: string
  email: string
  joinedAt: string
  paid?: boolean
}

interface Pool {
  poolId: string
  name: string
  description: string
  type: string
  status: 'active' | 'completed' | 'cancelled'
  startDate: string
  currentParticipants: number
  maxParticipants: number
  entryFee: number
  settings: {
    track: string
    raceDate: string
    totalRaces: number
  }
  races: Race[]
  participants: Participant[]
}

interface RaceResultModal {
  isOpen: boolean
  race: Race | null
}

interface ScratchModal {
  isOpen: boolean
  race: Race | null
  horse: Horse | null
}

export default function HorseRacingPoolDetailPage() {
  const { poolId } = useParams<{ poolId: string }>()
  const auth0 = useAuth0()
  const { getAccessTokenSilently } = auth0
  const navigate = useNavigate()
  
  const [pool, setPool] = useState<Pool | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [resultModal, setResultModal] = useState<RaceResultModal>({ isOpen: false, race: null })
  const [scratchModal, setScratchModal] = useState<ScratchModal>({ isOpen: false, race: null, horse: null })
  const [submitting, setSubmitting] = useState(false)
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; step: 'confirm' | 'verify' }>({ isOpen: false, step: 'confirm' })
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [updatingPayment, setUpdatingPayment] = useState<string | null>(null)
  
  const poolsApi = new PoolsApi(auth0)

  useEffect(() => {
    if (poolId) {
      fetchPool()
    }
  }, [poolId])

  const fetchPool = async () => {
    try {
      setLoading(true)
      const token = await getAccessTokenSilently()
      
      const response = await fetch(`${API_BASE_URL}/pools/${poolId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch pool: ${response.statusText}`)
      }

      const data = await response.json()
      
      // Check if this is a football pool and redirect to correct route
      if (data.pool?.type === 'football') {
        navigate(`/admin/pools/football/${poolId}`)
        return
      }
      
      setPool(data.pool)
    } catch (err) {
      console.error('Error fetching pool:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch pool')
    } finally {
      setLoading(false)
    }
  }


  const setRaceResult = async (raceNumber: number, winner: string, place?: string, show?: string) => {
    try {
      setSubmitting(true)
      const token = await getAccessTokenSilently()
      
      const response = await fetch(`${API_BASE_URL}/pools/${poolId}/races/${raceNumber}/result`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ winner, place, show })
      })

      if (!response.ok) {
        throw new Error(`Failed to set race result: ${response.statusText}`)
      }

      toast.success('Race result updated successfully')
      await fetchPool() // Refresh the data
      setResultModal({ isOpen: false, race: null })
    } catch (err) {
      console.error('Error setting race result:', err)
      toast.error(err instanceof Error ? err.message : 'Failed to set race result')
    } finally {
      setSubmitting(false)
    }
  }

  const scratchHorse = async (raceNumber: number, horseId: string, horseName: string) => {
    try {
      setSubmitting(true)
      const token = await getAccessTokenSilently()
      
      const response = await fetch(`${API_BASE_URL}/pools/${poolId}/races/${raceNumber}/scratch`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ horseId, horseName })
      })

      if (!response.ok) {
        throw new Error(`Failed to scratch horse: ${response.statusText}`)
      }

      const result = await response.json()
      toast.success(`${horseName} scratched successfully`)
      
      if (result.emailsSent > 0) {
        toast.success(`Notified ${result.emailsSent} participants via email`)
      }
      
      await fetchPool() // Refresh the data
      setScratchModal({ isOpen: false, race: null, horse: null })
    } catch (err) {
      console.error('Error scratching horse:', err)
      toast.error(err instanceof Error ? err.message : 'Failed to scratch horse')
    } finally {
      setSubmitting(false)
    }
  }

  const deletePool = async () => {
    if (!pool || deleteConfirmText !== pool.name) return
    
    try {
      setSubmitting(true)
      const token = await getAccessTokenSilently()
      
      const response = await fetch(`${API_BASE_URL}/pools/${poolId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to delete pool: ${response.statusText}`)
      }

      toast.success(`Pool "${pool.name}" has been deleted`)
      setDeleteModal({ isOpen: false, step: 'confirm' })
      
      // Navigate back to pools list
      window.location.href = '/admin/pools/horse-racing'
      
    } catch (err) {
      console.error('Error deleting pool:', err)
      toast.error(err instanceof Error ? err.message : 'Failed to delete pool')
    } finally {
      setSubmitting(false)
    }
  }

  const handleMarkPaid = async (participant: Participant, paid: boolean) => {
    if (!poolId || updatingPayment) return

    try {
      setUpdatingPayment(participant.userId)
      await poolsApi.markParticipantPaid(poolId, participant.userId, paid)
      
      toast.success(`${participant.name} marked as ${paid ? 'paid' : 'unpaid'}`)
      
      // Refresh pool data
      await fetchPool()
    } catch (err) {
      console.error('Error updating payment status:', err)
      toast.error(err instanceof Error ? err.message : 'Failed to update payment status')
    } finally {
      setUpdatingPayment(null)
    }
  }

  const getRaceStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
      case 'running':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      case 'final':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    }
  }

  const isHorseScratched = (race: Race, horseId: string) => {
    return race.scratches?.includes(horseId) || false
  }

  const getWinnerName = (race: Race, horseId: string) => {
    const horse = race.entries.find(h => h.horse_id === horseId)
    return horse?.horse_name || horseId
  }

  if (loading) {
    return (
      <PoolAdminRoute>
        <div className="flex justify-center items-center min-h-screen">
          <LoadingSpinner size="lg" />
        </div>
      </PoolAdminRoute>
    )
  }

  if (error || !pool) {
    return (
      <PoolAdminRoute>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="text-red-600 dark:text-red-400 mb-4">{error || 'Pool not found'}</div>
            <Link
              to="/admin/pools/horse-racing"
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700"
            >
              Back to Pools
            </Link>
          </div>
        </div>
      </PoolAdminRoute>
    )
  }

  return (
    <PoolAdminRoute>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb Navigation */}
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link
                to="/pools"
                className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600 dark:text-gray-400 dark:hover:text-white"
              >
                <HomeIcon className="w-4 h-4 mr-2" />
                Pools
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRightIcon className="w-6 h-6 text-gray-400" />
                <Link
                  to="/admin/pools/horse-racing"
                  className="ml-1 text-sm font-medium text-gray-700 hover:text-blue-600 md:ml-2 dark:text-gray-400 dark:hover:text-white"
                >
                  Horse Racing Pools
                </Link>
              </div>
            </li>
            <li aria-current="page">
              <div className="flex items-center">
                <ChevronRightIcon className="w-6 h-6 text-gray-400" />
                <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2 dark:text-gray-400">
                  {pool.name}
                </span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Header */}
        <div className="md:flex md:items-center md:justify-between mb-8">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl sm:truncate">
              {pool.name}
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {pool.settings.track} • {pool.settings.raceDate} • {pool.races.length} races
            </p>
          </div>
          <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
            <Link
              to={`/admin/pools/horse-racing/${poolId}/results`}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              <CheckCircleIcon className="-ml-1 mr-2 h-5 w-5" />
              Set Results
            </Link>
            <Link
              to={`/admin/pools/horse-racing/${poolId}/picks`}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              <EyeIcon className="-ml-1 mr-2 h-5 w-5" />
              View Picks
            </Link>
            <Link
              to={`/admin/pools/horse-racing/${poolId}/standings`}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700"
            >
              <TrophyIcon className="-ml-1 mr-2 h-5 w-5" />
              Standings
            </Link>
            <Link
              to={`/admin/pools/horse-racing/${poolId}/edit`}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              <Cog6ToothIcon className="-ml-1 mr-2 h-5 w-5" />
              Edit Pool
            </Link>
            <button
              onClick={() => setDeleteModal({ isOpen: true, step: 'confirm' })}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <TrashIcon className="-ml-1 mr-2 h-5 w-5" />
              Delete Pool
            </button>
          </div>
        </div>

        {/* Pool Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TrophyIcon className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      Participants
                    </dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-white">
                      {pool.currentParticipants} / {pool.maxParticipants}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      Completed Races
                    </dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-white">
                      {pool.races.filter(r => r.status === 'final').length} / {pool.races.length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      Total Scratches
                    </dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-white">
                      {pool.races.reduce((sum, race) => sum + (race.scratches?.length || 0), 0)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClockIcon className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      Prize Pool
                    </dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-white">
                      ${pool.entryFee * pool.currentParticipants}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Participants Management */}
        {pool.participants && pool.participants.length > 0 && (
          <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md mb-8">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white flex items-center">
                  <UserGroupIcon className="h-5 w-5 mr-2" />
                  Participants & Payment Management
                </h3>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {pool.participants.filter(p => p.paid).length} / {pool.participants.length} paid
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pool.participants.map((participant) => (
                  <div
                    key={participant.userId}
                    className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:shadow-md transition-shadow"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {participant.name}
                        </div>
                        {participant.paid ? (
                          <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                        ) : (
                          <ClockIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
                        )}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {participant.email}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Joined {new Date(participant.joinedAt).toLocaleDateString()}
                      </div>
                    </div>
                    
                    {/* Payment Controls */}
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handleMarkPaid(participant, !participant.paid)}
                        disabled={updatingPayment === participant.userId}
                        className={`px-3 py-1 text-xs font-medium rounded-md transition-colors whitespace-nowrap ${
                          participant.paid
                            ? 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900 dark:text-red-200 dark:hover:bg-red-800'
                            : 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900 dark:text-green-200 dark:hover:bg-green-800'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {updatingPayment === participant.userId ? (
                          'Updating...'
                        ) : participant.paid ? (
                          'Mark Unpaid'
                        ) : (
                          'Mark Paid'
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Races List */}
        <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
              Race Management
            </h3>

            <div className="space-y-4">
              {pool.races.map((race) => (
                <div key={race.raceNumber} className="border dark:border-gray-600 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-4">
                      <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                        Race {race.raceNumber}
                      </h4>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRaceStatusColor(race.status)}`}>
                        {race.status}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {race.postTime}
                      </span>
                    </div>
                    <div className="flex space-x-2">
                      {race.status !== 'final' && (
                        <button
                          onClick={() => setResultModal({ isOpen: true, race })}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                        >
                          Set Result
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Type:</span>
                      <div className="font-medium">{race.raceType}</div>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Distance:</span>
                      <div className="font-medium">{race.distance || 'N/A'}</div>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Purse:</span>
                      <div className="font-medium">${race.purse}</div>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Entries:</span>
                      <div className="font-medium">{race.entryCount}</div>
                    </div>
                  </div>

                  {/* Race Results */}
                  {race.status === 'final' && (
                    <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-md">
                      <h5 className="font-medium text-green-800 dark:text-green-200 mb-2">Results</h5>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                        {race.winner && (
                          <div>
                            <span className="text-green-700 dark:text-green-300">Win:</span>
                            <span className="ml-1 font-medium">{getWinnerName(race, race.winner)}</span>
                          </div>
                        )}
                        {race.placeFinisher && (
                          <div>
                            <span className="text-green-700 dark:text-green-300">Place:</span>
                            <span className="ml-1 font-medium">{getWinnerName(race, race.placeFinisher)}</span>
                          </div>
                        )}
                        {race.showFinisher && (
                          <div>
                            <span className="text-green-700 dark:text-green-300">Show:</span>
                            <span className="ml-1 font-medium">{getWinnerName(race, race.showFinisher)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Scratches */}
                  {race.scratches && race.scratches.length > 0 && (
                    <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-md">
                      <h5 className="font-medium text-red-800 dark:text-red-200 mb-2">Scratched Horses</h5>
                      <div className="flex flex-wrap gap-2">
                        {race.scratches.map((horseId) => (
                          <span key={horseId} className="inline-flex px-2 py-1 text-xs font-medium bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 rounded">
                            {getWinnerName(race, horseId)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Horses */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Post
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Colors
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Horse
                          </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Jockey/Trainer
                </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Weight
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {race.entries.map((horse) => {
                          const isScratched = isHorseScratched(race, horse.horse_id)
                          return (
                            <tr key={horse.horse_id} className={isScratched ? 'opacity-50 bg-red-50 dark:bg-red-900/10' : ''}>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                {horse.post_position}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                {horse.post_position_color ? (
                                  <div className="flex items-center space-x-2">
                                    <div 
                                      className="w-6 h-6 rounded-full border border-gray-300 dark:border-gray-600 flex items-center justify-center text-xs font-bold"
                                      style={{
                                        backgroundColor: horse.post_position_color.background_color,
                                        color: horse.post_position_color.text_color
                                      }}
                                      title={horse.post_position_color.name}
                                    >
                                      {horse.post_position}
                                    </div>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                      {horse.post_position_color.name}
                                    </span>
                                  </div>
                                ) : (
                                  <span className="text-sm text-gray-500 dark:text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {horse.horse_name}
                                  {isScratched && (
                                    <span className="ml-2 inline-flex px-2 py-1 text-xs font-medium bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 rounded">
                                      SCRATCHED
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {horse.age_sex}
                                </div>
                              </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          <div>J: {horse.jockey}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">T: {horse.trainer}</div>
                        </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                {horse.weight}
                                {horse.medication && (
                                  <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                                    ({horse.medication})
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm">
                                {!isScratched && race.status !== 'final' && (
                                  <button
                                    onClick={() => setScratchModal({ isOpen: true, race, horse })}
                                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                  >
                                    Scratch
                                  </button>
                                )}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Race Result Modal */}
        {resultModal.isOpen && resultModal.race && (
          <RaceResultModal
            race={resultModal.race}
            onClose={() => setResultModal({ isOpen: false, race: null })}
            onSubmit={setRaceResult}
            submitting={submitting}
          />
        )}

        {/* Scratch Modal */}
        {scratchModal.isOpen && scratchModal.race && scratchModal.horse && (
          <ScratchModal
            race={scratchModal.race}
            horse={scratchModal.horse}
            onClose={() => setScratchModal({ isOpen: false, race: null, horse: null })}
            onSubmit={scratchHorse}
            submitting={submitting}
          />
        )}

        {/* Delete Pool Modal */}
        {deleteModal.isOpen && pool && (
          <DeletePoolModal
            pool={pool}
            step={deleteModal.step}
            confirmText={deleteConfirmText}
            onConfirmTextChange={setDeleteConfirmText}
            onClose={() => {
              setDeleteModal({ isOpen: false, step: 'confirm' })
              setDeleteConfirmText('')
            }}
            onNextStep={() => setDeleteModal({ isOpen: true, step: 'verify' })}
            onSubmit={deletePool}
            submitting={submitting}
          />
        )}
      </div>
    </PoolAdminRoute>
  )
}

// Race Result Modal Component
function RaceResultModal({ 
  race, 
  onClose, 
  onSubmit, 
  submitting 
}: { 
  race: Race
  onClose: () => void
  onSubmit: (raceNumber: number, winner: string, place?: string, show?: string) => void
  submitting: boolean
}) {
  const [winner, setWinner] = useState('')
  const [place, setPlace] = useState('')
  const [show, setShow] = useState('')

  const availableHorses = race.entries.filter(horse => !race.scratches?.includes(horse.horse_id))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (winner) {
      onSubmit(race.raceNumber, winner, place || undefined, show || undefined)
    }
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Set Race {race.raceNumber} Result
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Winner (Required)
              </label>
              <select
                value={winner}
                onChange={(e) => setWinner(e.target.value)}
                className="w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:ring-yellow-500 focus:border-yellow-500"
                required
              >
                <option value="">Select winner...</option>
                {availableHorses.map((horse) => (
                  <option key={horse.horse_id} value={horse.horse_id}>
                    #{horse.post_position} {horse.post_position_color?.name ? `(${horse.post_position_color.name}) ` : ''}{horse.horse_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Place (2nd - Optional)
              </label>
              <select
                value={place}
                onChange={(e) => setPlace(e.target.value)}
                className="w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:ring-yellow-500 focus:border-yellow-500"
              >
                <option value="">Select place...</option>
                {availableHorses.filter(h => h.horse_id !== winner).map((horse) => (
                  <option key={horse.horse_id} value={horse.horse_id}>
                    #{horse.post_position} {horse.post_position_color?.name ? `(${horse.post_position_color.name}) ` : ''}{horse.horse_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Show (3rd - Optional)
              </label>
              <select
                value={show}
                onChange={(e) => setShow(e.target.value)}
                className="w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:ring-yellow-500 focus:border-yellow-500"
              >
                <option value="">Select show...</option>
                {availableHorses.filter(h => h.horse_id !== winner && h.horse_id !== place).map((horse) => (
                  <option key={horse.horse_id} value={horse.horse_id}>
                    #{horse.post_position} {horse.post_position_color?.name ? `(${horse.post_position_color.name}) ` : ''}{horse.horse_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!winner || submitting}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Setting...' : 'Set Result'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

// Scratch Modal Component
function ScratchModal({ 
  race, 
  horse, 
  onClose, 
  onSubmit, 
  submitting 
}: { 
  race: Race
  horse: Horse
  onClose: () => void
  onSubmit: (raceNumber: number, horseId: string, horseName: string) => void
  submitting: boolean
}) {
  const handleSubmit = () => {
    onSubmit(race.raceNumber, horse.horse_id, horse.horse_name)
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Scratch Horse
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Are you sure you want to scratch <strong>{horse.horse_name}</strong> from Race {race.raceNumber}?
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              This will automatically notify all participants who picked this horse via email.
            </p>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Scratching...' : 'Scratch Horse'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Delete Pool Modal Component
function DeletePoolModal({ 
  pool, 
  step,
  confirmText,
  onConfirmTextChange,
  onClose, 
  onNextStep,
  onSubmit, 
  submitting 
}: { 
  pool: Pool
  step: 'confirm' | 'verify'
  confirmText: string
  onConfirmTextChange: (text: string) => void
  onClose: () => void
  onNextStep: () => void
  onSubmit: () => void
  submitting: boolean
}) {
  const isConfirmTextValid = confirmText === pool.name

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {step === 'confirm' ? 'Delete Pool' : 'Confirm Deletion'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {step === 'confirm' ? (
            <div className="mb-4">
              <div className="flex items-center mb-4">
                <ExclamationTriangleIcon className="h-8 w-8 text-red-600 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Are you sure you want to delete this pool?
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    This action cannot be undone.
                  </p>
                </div>
              </div>
              
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3 mb-4">
                <div className="text-sm text-red-800 dark:text-red-200">
                  <p className="font-medium mb-1">This will permanently delete:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Pool: <strong>{pool.name}</strong></li>
                    <li>All {pool.races.length} races and entries</li>
                    <li>All participant picks and standings</li>
                    <li>All pool data and history</li>
                  </ul>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onNextStep}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Continue
                </button>
              </div>
            </div>
          ) : (
            <div className="mb-4">
              <div className="flex items-center mb-4">
                <ExclamationTriangleIcon className="h-8 w-8 text-red-600 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Final confirmation required
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Type the pool name to confirm deletion.
                  </p>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Type <strong>{pool.name}</strong> to confirm:
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => onConfirmTextChange(e.target.value)}
                  placeholder={pool.name}
                  className="w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:ring-red-500 focus:border-red-500"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onSubmit}
                  disabled={!isConfirmTextValid || submitting}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Deleting...' : 'Delete Pool'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
